import apiClient from './api.service';
import {
  Survey,
  CreateSurveyData,
  UpdateSurveyData,
  UpdateSurveyTagsData,
  SurveyVisibilityResponse,
  Question,
  CreateQuestionData,
  UpdateQuestionData,
  SurveyStatus,
  SurveyTakeResponse,
} from '../types/survey.types';
import { SurveyTagOption } from '../types/tag.types';
import { UpdateSurveyVisibilityRequest } from '../types/survey-visibility.types';
import { buildEffectiveChoices, normalizeQuestionOptions } from '../utils/questionOptions';
import { SurveyQuestionnaireReport } from '../types/survey-questionnaire-report.types';

// Phase 4 - US2: Survey Service (T077)

class SurveyService {
  private normalizeQuestion(question: Question): Question {
    const options = normalizeQuestionOptions(question.options);
    return {
      ...question,
      options,
      effectiveChoices:
        question.effectiveChoices && question.effectiveChoices.length > 0
          ? question.effectiveChoices
          : buildEffectiveChoices(options),
    };
  }

  private normalizeQuestionPayload(data: CreateQuestionData | UpdateQuestionData): any {
    if (!data.options) {
      return data;
    }

    return {
      ...data,
      options: normalizeQuestionOptions(data.options),
    };
  }

  async createSurvey(data: CreateSurveyData): Promise<Survey> {
    const response = await apiClient.post<{ data: Survey }>('/api/surveys', data);
    return response.data.data;
  }

  async getSurvey(id: string): Promise<Survey> {
    const response = await apiClient.get<{ data: Survey }>(`/api/surveys/${id}`);
    const survey = response.data.data;
    if (Array.isArray(survey.questions)) {
      survey.questions = survey.questions.map((question) => this.normalizeQuestion(question));
    }
    return survey;
  }

  async getSurveyForTaking(surveyId: string): Promise<SurveyTakeResponse> {
    const response = await apiClient.get<{ data: SurveyTakeResponse }>(`/api/surveys/${surveyId}/take`);
    const survey = response.data.data;

    return {
      ...survey,
      questions: survey.questions.map((question) => this.normalizeQuestion(question)),
    };
  }

  async listSurveys(params?: {
    status?: SurveyStatus;
    contractorId?: string;
    tagIds?: string[];
    page?: number;
    limit?: number;
  }): Promise<{ data: Survey[]; total: number; page: number; limit: number }> {
    const response = await apiClient.get<{
      data: Survey[];
      pagination: { page: number; limit: number; total: number };
    }>('/api/surveys', { params });
    return {
      data: response.data.data,
      ...response.data.pagination,
    };
  }

  async reprocessAnalysis(surveyId: string): Promise<{
    surveyId: number;
    status: string;
    analyzedResponseCount: number | null;
    generatedAt: string | null;
  }> {
    const response = await apiClient.post<{
      data: {
        surveyId: number;
        status: string;
        analyzedResponseCount: number | null;
        generatedAt: string | null;
      };
    }>(`/api/surveys/${surveyId}/analysis/reprocess`);
    return response.data.data;
  }

  async reprocessAnalysisNow(surveyId: string): Promise<{
    finalAnalysisId: string;
    versionId: string;
    status: string;
  }> {
    const response = await apiClient.post<{
      finalAnalysisId: string;
      versionId: string;
      status: string;
    }>(`/api/surveys/${surveyId}/analysis/reprocess-now`);
    return response.data;
  }

  async updateSurvey(id: string, data: UpdateSurveyData): Promise<Survey> {
    const response = await apiClient.put<{ data: Survey }>(
      `/api/surveys/${id}`,
      data
    );
    return response.data.data;
  }

  async getActiveSurveyTags(): Promise<SurveyTagOption[]> {
    const response = await apiClient.get<{ data: { items: SurveyTagOption[] } }>('/api/surveys/tags/active');
    return response.data.data.items;
  }

  async updateAdminSurveyTags(surveyId: string, data: UpdateSurveyTagsData): Promise<{ surveyId: string; tags: SurveyTagOption[] }> {
    const response = await apiClient.put<{ data: { surveyId: string; tags: SurveyTagOption[] } }>(
      `/api/admin/surveys/${surveyId}/tags`,
      data,
    );
    return response.data.data;
  }

  async updateContractorSurveyTags(surveyId: string, data: UpdateSurveyTagsData): Promise<{ surveyId: string; tags: SurveyTagOption[] }> {
    const response = await apiClient.put<{ data: { surveyId: string; tags: SurveyTagOption[] } }>(
      `/api/contractor/surveys/${surveyId}/tags`,
      data,
    );
    return response.data.data;
  }

  async getSurveyVisibility(id: string): Promise<SurveyVisibilityResponse> {
    const response = await apiClient.get<{ data: SurveyVisibilityResponse }>(`/api/surveys/${id}/visibility`);
    return response.data.data;
  }

  async updateSurveyVisibility(
    id: string,
    data: UpdateSurveyVisibilityRequest,
  ): Promise<SurveyVisibilityResponse> {
    const response = await apiClient.patch<{ data: SurveyVisibilityResponse }>(
      `/api/surveys/${id}/visibility`,
      data,
    );
    return response.data.data;
  }

  async addQuestion(
    surveyId: string,
    data: CreateQuestionData
  ): Promise<Question> {
    const payload = this.normalizeQuestionPayload(data);
    const response = await apiClient.post<{ data: Question }>(
      `/api/surveys/${surveyId}/questions`,
      payload
    );
    return this.normalizeQuestion(response.data.data);
  }

  async getQuestions(surveyId: string): Promise<Question[]> {
    const response = await apiClient.get<{ data: Question[] }>(
      `/api/surveys/${surveyId}/questions`
    );
    return response.data.data.map((question) => this.normalizeQuestion(question));
  }

  async updateQuestion(
    surveyId: string,
    questionId: string,
    data: UpdateQuestionData
  ): Promise<Question> {
    const payload = this.normalizeQuestionPayload(data);
    const response = await apiClient.put<{ data: Question }>(
      `/api/surveys/${surveyId}/questions/${questionId}`,
      payload
    );
    return this.normalizeQuestion(response.data.data);
  }

  async deleteQuestion(surveyId: string, questionId: string): Promise<void> {
    await apiClient.delete(`/api/surveys/${surveyId}/questions/${questionId}`);
  }

  /**
   * Check if user has already responded to a survey
   * This is used to determine if participant can respond or only view their response
   */
  async getQuestionnaireReport(surveyId: string): Promise<SurveyQuestionnaireReport> {
    const response = await apiClient.get<SurveyQuestionnaireReport>(
      `/api/surveys/${surveyId}/questionnaire-report`
    );
    return response.data;
  }

  async hasUserResponded(surveyId: string): Promise<boolean> {
    try {
      // Try to validate eligibility - if it succeeds, user hasn't responded
      await apiClient.get(`/api/surveys/${surveyId}/eligibility`);
      return false;
    } catch (error: any) {
      // Check if error is 409 Conflict (already responded)
      if (error.response?.status === 409) {
        return true;
      }
      
      // Check error message for "already responded"
      const message = error.response?.data?.message?.toLowerCase() || '';
      const errorText = error.response?.data?.error?.toLowerCase() || '';
      
      if (message.includes('already responded') || errorText.includes('conflict')) {
        return true;
      }
      
      // If it's a different error (not eligibility-related), return false
      return false;
    }
  }
}

export const surveyService = new SurveyService();
